import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { X } from "lucide-react";

const EMPTY_ITEM = {
    designer: "",
    category: "Outerwear",
    size: "",
    color: "",
    condition: "Excellent",
    description: "",
    original_price: "",
    asking_price: "",
    notes: "",
    images: [],
};

const CONDITIONS = ["Pristine", "Excellent", "Very Good", "Good"];
const CATEGORIES = ["Outerwear", "Tailoring", "Dresses", "Tops", "Knitwear", "Trousers", "Shoes", "Bags", "Accessories"];

async function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

async function compressImage(file, maxW = 1200) {
    // Downscale via canvas to keep base64 payload reasonable
    const dataUrl = await fileToDataUrl(file);
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const scale = Math.min(1, maxW / img.width);
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", 0.82));
        };
        img.src = dataUrl;
    });
}

export default function SellWithUs() {
    const [step, setStep] = useState(0); // 0 info · 1 form · 2 done
    const [contact, setContact] = useState({ full_name: "", email: "", phone: "", return_address: "" });
    const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");

    const updateItem = (i, patch) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
    const removeItem = (i) => setItems((prev) => prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i));
    const addItem = () => setItems((prev) => [...prev, { ...EMPTY_ITEM }]);

    const onFiles = async (i, files) => {
        const compressed = await Promise.all(Array.from(files).slice(0, 8).map((f) => compressImage(f)));
        updateItem(i, { images: [...items[i].images, ...compressed].slice(0, 8) });
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setSubmitting(true);
        try {
            const payload = {
                ...contact,
                items: items.map((it) => ({
                    ...it,
                    original_price: it.original_price ? Number(it.original_price) : null,
                    asking_price: it.asking_price ? Number(it.asking_price) : null,
                })),
            };
            await api.post("/consignments", payload);
            setStep(2);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (step === 2) {
        return (
            <div className="content-page" data-testid="sell-success">
                <div className="eyebrow" style={{ marginBottom: 16 }}>Submission Received</div>
                <h1>Thank you, <em>lovely.</em></h1>
                <p className="lede">
                    We&rsquo;ve got your pieces and our curators will review them within a few working
                    days. Look out for a note from us at {contact.email || "your inbox"}.
                </p>
                <div style={{ marginTop: 24 }}>
                    <Link to="/shop" className="btn">Explore The Edit</Link>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="sell-page">
            {/* Hero */}
            <section className="sell-hero">
                <div>
                    <div className="eyebrow" style={{ marginBottom: 16 }}>Sell With Us</div>
                    <h1 className="h-display">Consign what earns <em>the rack.</em></h1>
                    <p className="body-copy" style={{ marginTop: 20, maxWidth: 520 }}>
                        A short, considered list of clients receive each drop. Consign the pieces you no
                        longer wear to that audience — we handle photography, styling, and the sale.
                        You keep the majority.
                    </p>
                    <div style={{ marginTop: 24, display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <a href="#form" className="btn" data-testid="sell-start-btn">Start a Submission</a>
                        <Link to="/how-it-works" className="btn btn-outline">How It Works</Link>
                    </div>
                    <p style={{ marginTop: 24, fontFamily: "var(--font-serif)", fontStyle: "italic", color: "var(--cocoa-soft)" }}>
                        Commission split · 70% you / 30% RAX
                    </p>
                </div>
                <img
                    src="https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1600&q=80"
                    alt="Curated wardrobe rack"
                />
            </section>

            {/* Steps */}
            <div className="sell-steps">
                <div className="sell-steps-inner">
                    {[
                        { n: "I.", t: "Submit", b: "Tell us about your pieces and share photographs — front, back, label, and any wear." },
                        { n: "II.", t: "We review", b: "Our curators check each piece against the brief. We reply within a few days." },
                        { n: "III.", t: "We photograph & list", b: "Accepted pieces are styled, photographed, and placed on the Edit at a considered price." },
                        { n: "IV.", t: "You're paid", b: "When your piece sells, we notify you and settle in your currency of choice." },
                    ].map((s, i) => (
                        <div key={i} className="sell-step">
                            <div className="num">{s.n}</div>
                            <h3>{s.t}</h3>
                            <p>{s.b}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Form */}
            <form className="sell-form" id="form" onSubmit={submit} data-testid="sell-form">
                <div className="eyebrow" style={{ marginBottom: 16 }}>The Submission</div>
                <h2 className="h-section" style={{ marginBottom: 32 }}>Your details</h2>

                <div className="field">
                    <label className="label">Full name</label>
                    <input className="input" required value={contact.full_name} onChange={(e) => setContact({ ...contact, full_name: e.target.value })} data-testid="sell-name" />
                </div>
                <div className="field">
                    <label className="label">Email</label>
                    <input className="input" type="email" required value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="sell-email" />
                </div>
                <div className="field">
                    <label className="label">Phone</label>
                    <input className="input" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="sell-phone" />
                </div>
                <div className="field">
                    <label className="label">Return address</label>
                    <textarea className="input" rows="3" value={contact.return_address} onChange={(e) => setContact({ ...contact, return_address: e.target.value })} data-testid="sell-address" />
                </div>

                <h2 className="h-section" style={{ margin: "40px 0 20px" }}>Your pieces</h2>

                {items.map((it, i) => (
                    <div key={i} className="sell-form-item" data-testid={`sell-item-${i}`}>
                        <div className="sell-form-item-head">
                            <h4>Item {i + 1}</h4>
                            {items.length > 1 && (
                                <button type="button" className="sell-form-item-remove" onClick={() => removeItem(i)}>
                                    Remove
                                </button>
                            )}
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <div className="field">
                                <label className="label">Designer / Brand</label>
                                <input className="input" required value={it.designer} onChange={(e) => updateItem(i, { designer: e.target.value })} data-testid={`sell-item-designer-${i}`} />
                            </div>
                            <div className="field">
                                <label className="label">Category</label>
                                <select className="input" value={it.category} onChange={(e) => updateItem(i, { category: e.target.value })}>
                                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="field">
                                <label className="label">Size</label>
                                <input className="input" required value={it.size} onChange={(e) => updateItem(i, { size: e.target.value })} />
                            </div>
                            <div className="field">
                                <label className="label">Colour</label>
                                <input className="input" value={it.color} onChange={(e) => updateItem(i, { color: e.target.value })} />
                            </div>
                            <div className="field">
                                <label className="label">Condition</label>
                                <select className="input" value={it.condition} onChange={(e) => updateItem(i, { condition: e.target.value })}>
                                    {CONDITIONS.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="field">
                                <label className="label">Original retail (£)</label>
                                <input className="input" type="number" value={it.original_price} onChange={(e) => updateItem(i, { original_price: e.target.value })} />
                            </div>
                            <div className="field">
                                <label className="label">Asking (£)</label>
                                <input className="input" type="number" value={it.asking_price} onChange={(e) => updateItem(i, { asking_price: e.target.value })} />
                            </div>
                        </div>

                        <div className="field">
                            <label className="label">Description</label>
                            <textarea className="input" rows="3" value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} placeholder="Fit, era, styling notes…" />
                        </div>

                        <div className="field">
                            <label className="label">Photos (front · back · label · any wear)</label>
                            <label className="upload-dropzone" data-testid={`sell-item-photo-drop-${i}`}>
                                Drop photos here or click to choose — up to 8 images
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    style={{ display: "none" }}
                                    onChange={(e) => onFiles(i, e.target.files)}
                                />
                            </label>
                            {it.images.length > 0 && (
                                <div className="upload-previews">
                                    {it.images.map((src, idx) => (
                                        <div key={idx} className="upload-preview">
                                            <img src={src} alt="preview" />
                                            <button
                                                type="button"
                                                onClick={() => updateItem(i, { images: it.images.filter((_, x) => x !== idx) })}
                                                aria-label="Remove photo"
                                            >
                                                <X size={12} strokeWidth={1.6} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                <button type="button" className="btn btn-outline" onClick={addItem} data-testid="sell-add-item">
                    + Add another piece
                </button>

                {err && <div className="form-error" style={{ marginTop: 20 }}>{err}</div>}

                <div style={{ marginTop: 32 }}>
                    <button className="btn" type="submit" disabled={submitting} style={{ width: "100%" }} data-testid="sell-submit">
                        {submitting ? "Sending…" : "Submit for review"}
                    </button>
                    <p style={{ marginTop: 16, fontFamily: "var(--font-serif)", fontStyle: "italic", color: "var(--cocoa-soft)", textAlign: "center", fontSize: 14 }}>
                        By submitting you agree to our{" "}
                        <Link to="/legal/consignor" className="link-underline">Consignor Agreement</Link>.
                    </p>
                </div>
            </form>
        </div>
    );
}
